import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";
import {
  useFetchAllRidersQuery,
  useUpdateOrderRiderMutation,
} from "../../store";
import { IoChevronDown } from "react-icons/io5";
import { Rider } from "../../types";
import useDebounce from "../../hooks/useDebounce";
import Skeleton from "./Skeleton";
import { showToast, ToastType } from "../../utils/toast.utils";

interface RiderSelectProps {
  orderId: string;
  value?: { _id: string; fullName: string } | null;
  placeholder?: string;
  className?: string;
}

const RiderSelect: React.FC<RiderSelectProps> = ({
  orderId,
  value,
  placeholder,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showAll, setShowAll] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const debouncedSearchTerm = useDebounce(searchText, 400);

  // Fetch riders
  const queryParam = showAll ? "" : debouncedSearchTerm;
  const { data: riders = [], isFetching: isFetchingRiders } =
    useFetchAllRidersQuery(queryParam, {
      skip: !showAll && debouncedSearchTerm.length < 2,
    });

  const [updateOrderRider, { isLoading: isUpdating }] =
    useUpdateOrderRiderMutation();

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setShowAll(false);
  };

  const handleOptionClick = async (rider: Rider) => {
    setIsOpen(false);
    setSearchText("");
    setShowAll(false);
    const result = await updateOrderRider({ orderId, riderId: rider._id });
    if (!result.error) {
      showToast("Livreur mis à jour", ToastType.SUCCESS);
    }
  };

  const handleToggleDropdown = () => {
    setShowAll(!showAll);
    setIsOpen(!isOpen);
    inputRef.current?.focus();
  };

  // Close on outside click
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        !containerRef.current ||
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowAll(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  // Open dropdown if typing or showAll
  useEffect(() => {
    if (debouncedSearchTerm.length > 1 || showAll) setIsOpen(true);
    else setIsOpen(false);
  }, [debouncedSearchTerm, showAll]);

  // Get input position for portal
  const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({});
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyles({
        position: "absolute",
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 200), // <- set minimum width
        minWidth: 300, // optional, ensures long names fit
        zIndex: 9999,
      });
    }
  }, [isOpen]);

  const wrapperClasses = classNames(
    "w-full relative flex flex-col z-10 justify-center",
    className
  );

  const inputContainerClasses = twMerge(
    classNames(
      "w-full relative flex items-center rounded p-2 border bg-white cursor-text",
      isFocused ? "ring-2 ring-primary" : "border-gray-300"
    )
  );

  const inputClasses = classNames(
    "w-full text-sm focus:outline-none placeholder-gray-400 italic"
  );

  const renderedOptions = riders?.map((rider) => (
    <div
      key={rider._id}
      className="hover:bg-gray-200 rounded cursor-pointer p-1"
      onMouseDown={() => handleOptionClick(rider)} // prevent blur before click
    >
      {rider.fullName}
    </div>
  ));

  return (
    <div
      ref={containerRef}
      className={wrapperClasses}
    >
      <div className={inputContainerClasses}>
        <input
          ref={inputRef}
          value={
            isFocused ? searchText : searchText || value?.fullName || "livreur"
          }
          placeholder={placeholder || "livreur"}
          className={inputClasses}
          onChange={handleOnChange}
          autoComplete="off"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
        {isUpdating && (
          <span className="text-xs text-gray-400 ml-1">Mise à jour...</span>
        )}
        <button
          type="button"
          onClick={handleToggleDropdown}
          className="ml-1 p-1 hover:bg-gray-100 rounded transition"
        >
          <IoChevronDown
            className={classNames("text-gray-600 transition-transform", {
              "rotate-180": isOpen && showAll,
            })}
            size={16}
          />
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            style={dropdownStyles}
            className="border bg-white shadow-xl rounded-b max-h-60 overflow-y-auto"
          >
            {isFetchingRiders ? (
              <Skeleton
                className="h-8 w-full"
                times={3}
              />
            ) : renderedOptions && renderedOptions.length ? (
              renderedOptions
            ) : (
              <div className="text-gray-500 italic p-2">
                Aucun livreur trouvé
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default RiderSelect;
